<template>
  <div class="signup-container">
    <SignupFormVersion
      tracking="signup"
      :button-class=" $config.platform === 'fa' ? 'btn-primary-yellow' : 'btn-primary'"
      :title="$config.platform === 'fa' ? 'Start Learning For Free' : 'Create Your Free Account'"
    />
    <div>
      <div v-if="$store.state.campaign === 'free-weeks-2024'" class="free-campaign" :class="$config.platform">
        <div class="main">
          <span class="highlight blue">ALL {{ $config.platform === 'ds' ? 'DATA & AI' : 'FINANCE' }}</span>
          <span class="mobile-block">COURSES</span>
          <span class="block">
            <component :is="$config.platform === 'ds' ? 'Free' : 'FreeFa'" />
            <span class="mobile-block">for 21 Days</span>
          </span>
        </div>
        <div class="description">
          Learn at no cost with a 365 Free Plan. Sign up now and get unlimited access to all courses, projects, exams, and certificates for free until Nov. 21.
        </div>
        <div class="timer">
          <Countdown slot="countdown" :end-date="countdownEndDate" classes="bordered" />
        </div>
        <span class="no-card-required">
          <IconCreditCardCircle />
          <span>No credit card required</span>
        </span>
      </div>
      <div v-else class="info-wrap">
        <h2 class="h1">
          Welcome To Our Community
        </h2>
        <p class="subtitle">
          Learn. Connect. Share your experience with more than 3 million students across the globe.
        </p>
        <p class="students">
          <img :src="getImg" alt="signup-users">
          <span>Join {{ $c.text('students.count') }} Students</span>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { CAMPAIGN_PROMOTIONS } from '@itbpbg/lms-components/src/utils/campaigns';
import { getPromotion } from '@itbpbg/lms-components/src/utils/helper';
import global from '@itbpbg/lms-components/src/mixins/global';
import Free from '@itbpbg/lms-components/src/assets/svg/ds-fw2024-free.svg?vue-component';
import FreeFa from '@itbpbg/lms-components/src/assets/svg/fa-fw2024-free.svg?vue-component';
import Countdown from '@itbpbg/lms-components/src/Countdown';
import SignupFormVersion from '../components/Profile/Signup/SignupFormVersion';
import IconCreditCardCircle from '../assets/svg/icon-credit-card-circle.svg?vue-component';

export default {
  components: {
    SignupFormVersion,
    Free,
    FreeFa,
    Countdown,
    IconCreditCardCircle,
  },
  mixins: [global],
  middleware({ store, redirect }) {
    if (store.state.auth.user) {
      redirect('/');
    }
  },
  async asyncData({ $axios, route, $config }) {
    const page = await $axios.$get(`/pages/${route.fullPath}`).catch(() => ({}));
    page.fullPath = `${$config.siteUrl}${route.fullPath}`;

    return {
      page,
    };
  },
  data() {
    return {
      countdownEndDate: this.saleEndDate(),
      promotion: {},
    };
  },
  head() {
    const linkedData = this.$linkedDataLoad([
      { key: 'WebSite' },
      { key: 'WebPage', values: this.page },
    ]);

    return {
      titleTemplate: `${this.page.metaTitle}%s`,
      link: [
        { rel: 'canonical', href: this.canonicalURL() },
      ],
      meta: this.metaProps(this.page),
      script: [linkedData],
    };
  },
  computed: {
    getImg() {
      if (this.$config.platform === 'fa') {
        return require('../assets/images/fa/fa-signup-users.png');
      }
      return require('../assets/images/signup-users.png');
    },
  },
  created() {
    this.countdownEndDate = this.saleEndDate();
  },
  methods: {
    saleEndDate() {
      const campaignData = CAMPAIGN_PROMOTIONS['free-weeks-2024'];
      const promoOptions = getPromotion(campaignData.promos, this.promotion);
      this.promotion = {
        buttonText: 'Start Now',
        ...promoOptions.nextPromotion,
        ...campaignData.templates,
      };
      if (promoOptions.refresh) {
        setTimeout(() => {
          window.$nuxt.refresh();
        }, 40000);
      }

      return new Date(this.promotion.endDate);
    },
  },
};

</script>
<style lang="scss">
.signup-container {
  min-height: 100vh;
  position: relative;

  .free-campaign {
    color: var(--main-light);
    width: 570px;

    .main {
      font-size: 32px;

      svg {
        height: 106px;
      }

      .highlight {
        font-weight: 700;
        color: var(--main-light);

        &.campaign-1,
        &.blue {
          color: var(--c-fw2024-1);

          &.special {
            text-shadow: 4px 4px 0 var(--main-dark);
          }
        }

        &.campaign-2,
        &.yellow {
          color: var(--c-fw2024-2);

          &.special {
            text-shadow: -1px -1px 0 var(--main-dark), 1px -1px 0 var(--main-dark), -1px 1px 0 var(--main-dark), 1px 1px 0 var(--main-dark), 4px 4px 0 var(--c-lda-2);
          }
        }

        &.white {
          font-weight: 600;
          color: var(--main-light);
        }

        &.thin {
          font-weight: inherit;
        }
      }

      .highlight {
        font-size: 56px;
        font-weight: 800;
      }

      > .mobile-block {
        font-weight: 800;
      }

      .block {
        .mobile-block {
          vertical-align: top;
          display: inline-block;
          margin-top: 20px;
        }
      }
    }

    .description {
      margin: 28px 0 32px;
      font-size: 21px;
      line-height: 1.4;
    }

    .timer {
      .countdown {
        color: var(--main-light);

        .box {
          border-radius: 0;
        }
      }
    }

    .no-card-required {
      display: flex;
      align-items: center;
      font-size: 21px;
      margin-top: 20px;

      svg {
        margin-right: 8px;
      }
    }

    .block {
      display: block;
    }

    &.fa {
      .timer .countdown .box {
        border-color: #68A9FE;
      }
    }
  }

  .form-wrap {
    padding-top: var(--spacing-60);
    padding-left: 0;
    padding-right: 0;
    max-width: 332px;

    h1 {
      margin-bottom: var(--spacing-24);
    }
  }

  > div {
    min-height: inherit;
    float: left;
    -webkit-box-align: center;
    -ms-flex-align: center;
    -webkit-align-items: center;
    align-items: center;
    display: -webkit-box;
    display: -webkit-flex;
    display: -ms-flexbox;
    display: flex;

    > div {
      margin: auto;
    }

    &:first-of-type {
      background-color: var(--main-light);
      width: 46%;
    }

    &:last-of-type {
      width: 54%;
      background: linear-gradient(-253.23deg, rgba(0, 164, 178, 0.2) 0%, rgba(0, 164, 178, 0.05) 44.75%, rgba(0, 164, 178, 0) 94.77%);
      background-color: var(--main-dark);
    }

    .form-wrap {
      margin: auto;
    }

    .info-wrap {
      max-width: 400px;
      color: var(--main-light);

      .h1 {
        margin: 0;
        font-size: 48px;
      }

      .subtitle {
        margin-bottom: var(--spacing-48);
        margin-top: var(--spacing-16);
      }

      .students {
        margin: 0;
      }

      img {
        vertical-align: middle;
        margin-right: var(--spacing-24);
      }
    }
  }

  @include bp($bp-laptop) {
    > div .info-wrap .h1 {
      font-size: 44px;
    }
  }

  @include bp($bp-mobile) {
    padding: 148px 0 0;
    min-height: calc(100vh - 148px);
    margin: 0;

    .mobile-block {
      display: block;
    }

    > div {
      align-items: flex-start;
      justify-content: center;

      .form-wrap {
        margin: 0;
        padding-top: 0;
      }

      .signup-options {
        margin: 0;

        .form-wrap {
          margin: 0 auto;
        }
      }

      &:first-of-type {
        width: 100%;
      }

      &:last-of-type {
        display: none;
      }
    }
  }
}

@include bp($bp-laptop) {
  .signup-container {
    .form-wrap {
      max-width: 310px;
    }

    .free-campaign {
      width: 480px;

      .main {
        font-size: 28px;

        svg {
          height: 90px;
        }

        .highlight {
          font-size: 48px;
        }
      }
    }
  }
}

.fa {
  .signup-container > div:last-of-type {
    background: linear-gradient(135deg, rgba(3, 111, 253, 0.14) 19.77%, rgba(3, 111, 253, 0.05) 36.77%, rgba(1, 22, 51, 0) 63.97%);
    background-color: var(--main-dark);
  }
}
</style>
